// BookCall ViewModel - gates the booking step to qualified creators, composes
// the prefilled HubSpot embed URL, listens for the booked event, and exposes
// skip/continue navigation back to the onboarding destination (GRW-9).

import { useEffect, useState } from 'react';
import { PATHS } from '@client/routes';
import { navigate } from '@client/new/helpers/history';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { makeAsync } from '@client/new/helpers/async';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useBookCallModel } from '../model/BookCall.model';
import { AmKey } from '../../view-model/amProfiles';
import { buildEmbedUrl, isMeetingBookedMessage } from './bookCallEmbed';

export type BookCallState = {
  amKey: AmKey;
  amDisplayName: string | null;
  embedUrl: string;
  booked: boolean;
};

type BookCallActionMap = {
  Skip: null;
  Continue: null;
};

export type BookCallVM = {
  state: BookCallState;
} & Dispatchers<BookCallActionMap>;

// The onward destination preserves the pre-booking-step flow: the strategy
// guide. The selected strategy is carried via the URL when routing in from
// strategy selection; absent, the guide resolves it from saved state.
const getOnwardUrl = (creatorId: number): string => {
  const strategyId = new URLSearchParams(window.location.search).get(
    'strategyId',
  );
  const base = PATHS.GUIDE(creatorId);
  return strategyId
    ? `${base}?${new URLSearchParams({ strategyId }).toString()}`
    : base;
};

export const useBookCallViewModel = (): Async<BookCallVM> => {
  const creatorId = useInfluencerId();
  const model = useBookCallModel();
  const [booked, setBooked] = useState(false);

  const notQualified = model.type === 'fetched' && !model.value.qualified;

  // Non-qualified creators never see the step: redirect onward as soon as the
  // gate resolves (covers deep links and direct URL access).
  useEffect(() => {
    if (notQualified) {
      navigate(getOnwardUrl(creatorId));
    }
  }, [notQualified, creatorId]);

  // HubSpot posts a window message when the meeting is booked.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (isMeetingBookedMessage(event.data)) {
        setBooked(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  if (model.type !== 'fetched') {
    return model;
  }

  const step = model.value;

  // While the redirect effect runs, keep showing the loader rather than
  // briefly rendering booking UI for a non-qualified creator.
  if (!step.qualified || !step.amKey || !step.meetingUrl) {
    return makeAsync.fetching();
  }

  const embedUrl = buildEmbedUrl(step.meetingUrl, {
    email: step.prefillEmail,
    firstName: step.prefillFirstName,
    lastName: null,
  });

  const _dispatch = (action: Actions<BookCallActionMap>) => {
    switch (action.type) {
      case 'Skip':
      case 'Continue':
        navigate(getOnwardUrl(creatorId));
        break;
    }
  };

  return toAsyncFetched({
    state: {
      amKey: step.amKey,
      amDisplayName: step.amDisplayName,
      embedUrl,
      booked,
    },
    ...getDispatchers<BookCallActionMap>(_dispatch),
  });
};
